<template>
  <SidebarProvider :default-open="false">
    <div class="flex w-full min-h-svh">
      <SidebarComponent />

      <div class="flex flex-col w-full">
        <MobileNavbar />

        <div
          class="flex flex-col flex-1 my-2 ml-2 lg:ml-0 gradient-borders rounded-3xl"
          :class="{
            'mr-14 md:mr-2': isTemplatePreview,
            'mr-[9px]': !isTemplatePreview,
          }"
        >
          <div
            class="flex flex-col flex-1 overflow-hidden gradient-background rounded-3xl"
          >
            <div
              class="z-10 flex flex-row items-center justify-between p-2 lg:border-b lg:border-custom-gray-500"
            >
              <Analytics />
              <SidebarTrigger
                :disabled="!loggedIn"
                class="hidden w-10 h-10 lg:flex"
              />

              <AuthButton class="hidden lg:flex" />
            </div>

            <main class="flex items-center justify-center flex-1">
              <slot />
            </main>

            <FooterComponent :locale="locale" class="relative my-4" />

            <LoginModal
              :open="signInModalOpened && !loggedIn"
              @update:open="toggleSignInModal"
            />
            <VerifyRequestModal
              :open="verifyRequestModalOpened"
              @update:open="toggleVerifyRequestModal"
            />
            <SettingsModal
              :open="settingsModalOpened"
              @update:open="toggleSettingsModal"
            />
            <FeedbackModal
              :open="feedbackModalOpened"
              @update:open="toggleFeedbackModal"
            />
            <NotificationsComponent />
          </div>
        </div>
      </div>
    </div>
  </SidebarProvider>
</template>

<script lang="ts" setup>
import { Analytics } from "@vercel/analytics/nuxt";
import FooterComponent from "~/components/Navigation/FooterComponent.vue";
import LoginModal from "~/components/Auth/LoginModal.vue";
import VerifyRequestModal from "~/components/Auth/VerifyRequestModal.vue";
import SettingsModal from "~/components/Modals/SettingsModal.vue";
import FeedbackModal from "~/components/Modals/FeedbackModal.vue";
import { useI18n } from "vue-i18n";
import { SidebarProvider } from "~/components/ui/sidebar";
import SidebarComponent from "~/components/Navigation/SidebarComponent.vue";
import AuthButton from "~/components/Navigation/AuthButton.vue";
import MobileNavbar from "~/components/Navigation/MobileNavbar.vue";
import NotificationsComponent from "~/components/NotificationsComponent.vue";

const { status } = useAuth();
const loggedIn = computed(() => status.value === "authenticated");

const route = useRoute();
const isTemplatePreview = computed(() => {
  return route.name === "chat-uuid" || route.name === "chat";
});

const {
  signInModalOpened,
  toggleSignInModal,

  toggleVerifyRequestModal,
  verifyRequestModalOpened,

  settingsModalOpened,
  toggleSettingsModal,

  feedbackModalOpened,
  toggleFeedbackModal,
} = useModals();

const { t, locale } = useI18n({
  useScope: "global",
  head: {
    // 👇 Enables i18n-based head meta generation
    seo: true,
  },
});

useHead({
  htmlAttrs: {
    lang: locale.value,
  },
  title: t("common.title"),
  meta: [
    { name: "author", content: "Topol" },
    {
      name: "description",
      content: t("common.description"),
    },
    {
      property: "og:description",
      content: t("common.description"),
    },
  ],
});
</script>

<style>
body {
  font-family: Geist, serif;
}

/* Hide the scrollbar track but keep the thumb */
::-webkit-scrollbar {
  width: 8px;
}

/* Make the track transparent */
::-webkit-scrollbar-track {
  background: transparent;
}

/* Dark mode scrollbar */
.dark ::-webkit-scrollbar-thumb {
  background: #494d55;
  border-radius: 4px;
}

.dark ::-webkit-scrollbar-thumb:hover {
  background: #494d55;
}

/* Light mode scrollbar */
.light ::-webkit-scrollbar-thumb {
  background: #f4f4f5;
  border-radius: 4px;
}

.light ::-webkit-scrollbar-thumb:hover {
  background: #f4f4f5;
}

.gradient-background {
  background: radial-gradient(
    64.15% 64.15% at 50% 13.79%,
    #1f2329 0%,
    #0f1217 70%,
    #0b0d11 98.56%
  );
}

.gradient-borders {
  position: relative;
  background: linear-gradient(180deg, #494d55 0%, #1f2329 100%);
  padding: 1px; /* creates the border thickness */

  /* Create the inner content */
}

:root {
  --size: 20px;
}

.noise {
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 1.5s ease-in; /* Smooth fade-in */
  background: conic-gradient(
    from 180deg at 50% 70%,
    hsla(0, 0%, 98%, 1) 0deg,
    #eec32d 72deg,
    #ec4b4b 144deg,
    #709ab9 216deg,
    #4dffbf 288deg,
    hsla(0, 0%, 98%, 1) 1turn
  );
  mask:
    radial-gradient(circle at 50% 50%, white 2px, transparent 2.5px) 50% 50% /
      var(--size) var(--size),
    url("https://assets.codepen.io/605876/noise-mask.png") 256px 50% / 256px
      256px;
  mask-composite: intersect;
  animation: flicker 20s infinite linear;
}

.noise.loaded {
  opacity: 1;
}

@keyframes flicker {
  to {
    mask-position:
      50% 50%,
      0 50%;
  }
}

.text-glow {
  text-shadow:
    0 0 10px rgb(192 219 255 / 55%),
    0 0 32px rgb(65 120 255 / 24%);
}
</style>
